package org.teddapp.project.ui.portfolio

import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.interaction.MutableInteractionSource
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.heightIn
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.remember
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.platform.LocalUriHandler
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextOverflow
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import coil3.compose.AsyncImage
import com.mikepenz.markdown.m3.Markdown
import com.mikepenz.markdown.m3.markdownColor
import com.mikepenz.markdown.m3.markdownTypography
import com.tedd.db.Portfolio_piece
import org.teddapp.project.portfolio.PieceStatus
import org.teddapp.project.portfolio.PortfolioCodecs
import org.teddapp.project.ui.ChipSize
import org.teddapp.project.ui.ChipTone
import org.teddapp.project.ui.TeddButton
import org.teddapp.project.ui.TeddButtonKind
import org.teddapp.project.ui.TeddButtonSize
import org.teddapp.project.ui.TeddChip
import org.teddapp.project.ui.TeddDates
import org.teddapp.project.ui.TeddField
import org.teddapp.project.ui.TeddIcon
import org.teddapp.project.ui.TeddIconButton
import org.teddapp.project.ui.TeddTokens
import org.teddapp.project.ui.TeddType
import androidx.compose.foundation.layout.widthIn
import org.teddapp.project.ui.TeddTab
import org.teddapp.project.ui.TeddTabs
import org.teddapp.project.ui.TeddTag
import org.teddapp.project.ui.TeddVisibility
import org.teddapp.project.ui.TeddVisibilityBadge
import org.teddapp.project.ui.TeddEmptyState
import org.teddapp.project.ui.TeddSpace
import org.teddapp.project.ui.TeddSize
import org.teddapp.project.ui.teddClickable
import org.teddapp.project.ui.teddCardShadow

/**
 * Enregistre le chargeur d'images Coil 3 (réseau via Ktor) pour les images par URL
 * des pièces. Idempotent : `setSingletonImageLoaderFactory` ne s'applique qu'une fois.
 */
@Composable
internal fun rememberPortfolioImageLoader() {
    coil3.compose.setSingletonImageLoaderFactory { ctx ->
        coil3.ImageLoader.Builder(ctx)
            .components { add(coil3.network.ktor3.KtorNetworkFetcherFactory()) }
            .build()
    }
}

internal fun periodLabel(start: String?, end: String?): String? {
    val s = TeddDates.formatShort(start, withYear = true)
    val e = TeddDates.formatShort(end, withYear = true)
    return when {
        s != null && e != null -> "$s → $e"
        s != null -> "depuis $s"
        e != null -> "jusqu'au $e"
        else -> null
    }
}

/** En-tête de la liste des pièces : compte, bouton, filtre, recherche. */
@Composable
internal fun PiecesHeader(model: PortfolioModel, compact: Boolean) {
    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S3), modifier = Modifier.fillMaxWidth()) {
        Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2)) {
            Column(modifier = Modifier.weight(1f).padding(start = if (compact) 0.dp else TeddSpace.S2)) {
                Text("Pièces", style = TeddType.BodyStrong, color = TeddTokens.Ink)
                Text(
                    text = "${model.pieces.size} · ${model.publishedCount} publiée${if (model.publishedCount > 1) "s" else ""}",
                    style = TeddType.MonoSm.copy(fontSize = 11.sp),
                    color = TeddTokens.InkMuted,
                )
            }
            TeddButton(
                text = if (compact) "" else "Nouvelle pièce",
                onClick = { model.editor = PortfolioEditor.Piece(null) },
                kind = TeddButtonKind.Secondary,
                size = TeddButtonSize.Sm,
                icon = "plus",
            )
        }
        TeddTabs(
            tabs = PieceFilter.entries.map { TeddTab(it.name, it.label) },
            selected = model.pieceFilter.name,
            onSelect = { model.pieceFilter = PieceFilter.valueOf(it) },
        )
        if (model.pieces.size > 4) {
            TeddField(label = "Recherche", value = model.search, onValueChange = { model.search = it }, placeholder = "Titre, contexte, récit…")
        }
    }
}

/** Ligne d'une pièce dans la liste : celle qu'on lit se pose sur la surface. */
@Composable
internal fun PieceCard(p: Portfolio_piece, model: PortfolioModel, selected: Boolean, card: Boolean = false, onClick: () -> Unit) {
    val shape = RoundedCornerShape(TeddTokens.RMd)
    val published = p.status == PieceStatus.PUBLISHED
    val competences = model.linksOf(p.id).mapNotNull { model.competencesById[it.competence_id]?.name }.distinct()
    Column(
        modifier = Modifier
            .fillMaxWidth()
            .then(if (selected) Modifier.teddCardShadow(shape) else Modifier)
            .then(if (card) Modifier.border(1.dp, TeddTokens.Line, shape) else Modifier)
            .teddClickable(
                onClick = onClick,
                shape = shape,
                fill = if (selected || card) TeddTokens.Surface else Color.Transparent,
                hover = if (selected || card) TeddTokens.Surface else TeddTokens.SurfaceSunken,
                role = null,
            )
            .padding(horizontal = TeddSpace.S3, vertical = 10.dp),
        verticalArrangement = Arrangement.spacedBy(TeddSpace.S1),
    ) {
        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
            Text(p.title, color = TeddTokens.Ink, style = TeddType.BodyStrong, maxLines = 2, overflow = TextOverflow.Ellipsis, modifier = Modifier.weight(1f))
            if (p.pinned == 1L) TeddIcon(name = "pin", size = 13.dp, color = TeddTokens.ModPortfolio)
        }
        p.subtitle?.takeIf { it.isNotBlank() }?.let { Text(it, color = TeddTokens.InkMuted, style = TeddType.Caption, maxLines = 2, overflow = TextOverflow.Ellipsis) }
        Row(
            modifier = Modifier.padding(top = TeddSpace.S1),
            verticalAlignment = Alignment.CenterVertically,
            horizontalArrangement = Arrangement.spacedBy(TeddSpace.S2),
        ) {
            TeddVisibilityBadge(if (published) TeddVisibility.Public else TeddVisibility.Draft, label = if (published) "Publiée" else "Brouillon")
            Spacer(modifier = Modifier.weight(1f))
            Text(
                text = competences.size.takeIf { it > 0 }?.let { "$it compétence${if (it > 1) "s" else ""}" }
                    ?: periodLabel(p.date_start, p.date_end).orEmpty(),
                color = TeddTokens.InkMuted,
                style = TeddType.MonoSm.copy(fontSize = 11.sp),
                maxLines = 1,
                overflow = TextOverflow.Ellipsis,
            )
        }
    }
}

@Composable
internal fun PiecesList(model: PortfolioModel, compact: Boolean, onOpen: (Portfolio_piece) -> Unit) {
    val list = model.visiblePieces
    if (model.pieces.isEmpty()) {
        PortfolioEmpty(model)
        return
    }
    if (list.isEmpty()) {
        Text("Aucune pièce ne correspond.", color = TeddTokens.InkMuted, style = TeddType.Caption, modifier = Modifier.padding(TeddSpace.S2))
        return
    }
    list.forEach { p -> PieceCard(p, model, selected = !compact && p.id == model.selectedPieceId, card = compact) { onOpen(p) } }
}

@Composable
internal fun PortfolioEmpty(model: PortfolioModel) {
    TeddEmptyState(
        title = "Un portfolio vide",
        message = "Une pièce raconte une réalisation : le contexte, ce que tu as fait, ce que ça a produit, et les compétences que ça démontre. Brouillon d'abord, publiée quand elle est prête.",
        icon = "cards",
        actionLabel = "Écrire une pièce",
        onAction = { model.editor = PortfolioEditor.Piece(null) },
    )
}

/** Détail d'une pièce : récit rendu, liens, images, compétences démontrées. */
@Composable
internal fun PieceDetail(p: Portfolio_piece, model: PortfolioModel, compact: Boolean, onBack: (() -> Unit)? = null) {
    val published = p.status == PieceStatus.PUBLISHED
    val links = PortfolioCodecs.decodeLinks(p.links)
    val images = PortfolioCodecs.decodeImages(p.images)
    val demonstrated = model.linksOf(p.id)
    val uri = LocalUriHandler.current

    Column(verticalArrangement = Arrangement.spacedBy(TeddSpace.S6), modifier = Modifier.fillMaxWidth()) {
        if (onBack != null) {
            TeddButton(text = "Pièces", onClick = onBack, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm, icon = "chevron-left")
        }
        Column(verticalArrangement = Arrangement.spacedBy(8.dp)) {
            Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                TeddVisibilityBadge(if (published) TeddVisibility.Public else TeddVisibility.Draft, label = if (published) "Publiée" else "Brouillon")
                if (p.pinned == 1L) TeddTag("Épinglée", icon = "pin")
                Spacer(modifier = Modifier.weight(1f))
                TeddIconButton(onClick = { model.togglePinned(p) }, active = p.pinned == 1L) {
                    TeddIcon(name = "pin", size = 16.dp, color = if (p.pinned == 1L) TeddTokens.Ink else TeddTokens.InkMuted)
                }
                TeddButton(text = if (published) "Retirer du site" else "Publier sur le site", onClick = { model.togglePublished(p) }, kind = if (published) TeddButtonKind.Ghost else TeddButtonKind.Publish, size = TeddButtonSize.Sm, icon = if (published) null else "globe")
                TeddButton(text = "Modifier", onClick = { model.editor = PortfolioEditor.Piece(p) }, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm, icon = "edit")
                TeddIconButton(onClick = { model.confirmDelete = DeleteTarget.Piece(p) }) {
                    TeddIcon(name = "trash", size = 16.dp, color = TeddTokens.InkMuted)
                }
            }
            org.teddapp.project.ui.TeddFitText(p.title, style = if (compact) TeddType.Title2 else TeddType.Title1, minFontSize = 16.sp, maxLines = 3)
            p.subtitle?.takeIf { it.isNotBlank() }?.let { Text(it, style = TeddType.BodyLg, color = TeddTokens.InkMuted) }
            Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                periodLabel(p.date_start, p.date_end)?.let { PfMetaRow("calendar", it) }
                model.projectName(p.project_id)?.let { PfMetaRow("grid", "Projet · $it") }
                model.stepTitle(p.step_id)?.let { PfMetaRow("flag", "Étape · $it") }
            }
        }

        if (p.body.isNotBlank()) {
            Markdown(
                content = p.body,
                colors = markdownColor(text = TeddTokens.Ink),
                typography = markdownTypography(
                    h1 = TeddType.Title2,
                    h2 = TeddType.Title2.copy(fontSize = 18.sp, lineHeight = 24.sp),
                    h3 = TeddType.BodyStrong,
                    text = TeddType.BodyLg,
                    paragraph = TeddType.BodyLg,
                    ordered = TeddType.BodyLg,
                    bullet = TeddType.BodyLg,
                    list = TeddType.BodyLg,
                    quote = TeddType.Quote,
                ),
                modifier = Modifier.fillMaxWidth(),
            )
        }

        if (images.isNotEmpty()) {
            Column(verticalArrangement = Arrangement.spacedBy(10.dp)) {
                PfEyebrow("Images")
                images.forEach { img ->
                    Column(verticalArrangement = Arrangement.spacedBy(4.dp)) {
                        PieceImageView(
                            db = model.repos.db,
                            image = img,
                            modifier = Modifier
                                .fillMaxWidth()
                                .heightIn(min = 80.dp, max = if (compact) 220.dp else 360.dp)
                                .clip(RoundedCornerShape(TeddTokens.RMd)),
                        )
                        Row(verticalAlignment = Alignment.CenterVertically, horizontalArrangement = Arrangement.spacedBy(6.dp)) {
                            if (img.caption.isNotBlank()) Text(img.caption, color = TeddTokens.InkMuted, style = TeddType.Caption, modifier = Modifier.weight(1f))
                            else Spacer(modifier = Modifier.weight(1f))
                            if (!PieceImages.isFile(img.url)) TeddButton(text = "Ouvrir", onClick = { runCatching { uri.openUri(img.url) } }, kind = TeddButtonKind.Ghost, size = TeddButtonSize.Sm)
                        }
                    }
                }
            }
        }

        if (links.isNotEmpty()) {
            Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
                PfEyebrow("Liens")
                links.forEach { l ->
                    Row(
                        modifier = Modifier
                            .fillMaxWidth()
                            .clip(RoundedCornerShape(TeddTokens.RSm))
                            .background(TeddTokens.Surface, RoundedCornerShape(TeddTokens.RSm))
                            .border(1.dp, TeddTokens.Line, RoundedCornerShape(TeddTokens.RSm))
                            .teddClickable(onClick = { runCatching { uri.openUri(l.url) } }, role = null)
                            .padding(horizontal = 10.dp, vertical = 8.dp),
                        verticalAlignment = Alignment.CenterVertically,
                        horizontalArrangement = Arrangement.spacedBy(8.dp),
                    ) {
                        TeddIcon(name = "arrow-right", size = 14.dp, color = TeddTokens.InkMuted)
                        Column(modifier = Modifier.weight(1f)) {
                            Text(l.label.ifBlank { l.url }, color = TeddTokens.Ink, style = TeddType.Label, maxLines = 1, overflow = TextOverflow.Ellipsis)
                            if (l.label.isNotBlank()) Text(l.url, color = TeddTokens.InkMuted, style = TeddType.MonoSm.copy(fontSize = 11.sp), maxLines = 1, overflow = TextOverflow.Ellipsis)
                        }
                    }
                }
            }
        }

        Column(verticalArrangement = Arrangement.spacedBy(6.dp)) {
            PfEyebrow("Compétences démontrées · ${demonstrated.size}")
            if (demonstrated.isEmpty()) {
                Text("Aucune pour l'instant. Modifie la pièce pour en rattacher.", color = TeddTokens.InkMuted, style = TeddType.Body)
            }
            demonstrated.forEach { link ->
                val competence = model.competencesById[link.competence_id] ?: return@forEach
                val criterion = link.criterion_id?.let { id -> model.criteria.firstOrNull { it.id == id } }
                val level = criterion?.let { org.teddapp.project.portfolio.CompetenceLevel.of(it.level) } ?: model.levelOf(competence.id)
                Row(
                    modifier = Modifier
                        .fillMaxWidth()
                        .clip(RoundedCornerShape(TeddTokens.RSm))
                        .teddClickable(onClick = { model.openCompetence(competence.id) }, role = null)
                        .padding(horizontal = TeddSpace.S2, vertical = TeddSpace.S2),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(8.dp),
                ) {
                                        Column(modifier = Modifier.weight(1f)) {
                        Text(competence.name, color = TeddTokens.Ink, style = TeddType.BodyStrong, maxLines = 1, overflow = TextOverflow.Ellipsis)
                        val sub = listOfNotNull(criterion?.label, link.note?.takeIf { it.isNotBlank() }).joinToString(" · ")
                        if (sub.isNotBlank()) Text(sub, color = TeddTokens.InkMuted, style = TeddType.Caption, maxLines = 2, overflow = TextOverflow.Ellipsis)
                    }
                    LevelBadge(level, small = true)
                }
            }
        }
    }
}
